/**
 * 命例持久化层（Android 排盘客户端方案 · A 节）
 * 设计决策：持久化 BirthInput（输入参数）而非排盘结果——
 * 点击命例时重建盘（本地毫秒级），且规避引擎版本升级后的结果数据兼容问题。
 *
 * - CaseRecord：命例记录 = 排盘输入 + 摘要 + 名称/备注/分类（用户自定义管理）
 * - ChartStorage：存储抽象（Web 端 localStorage；App 端 @capacitor/preferences）
 * - LocalStorageStorage：Web 通用实现（浏览器验证用）
 * - MAX_HISTORY：记录上限，超出删最旧
 */
import type { BirthInput } from '../types/api';
import type { Meta } from '../types/base';

/** 单条命例：chartId 仅作标识，重建盘时以 input 为准 */
export interface CaseRecord {
  chartId: string;
  input: BirthInput;
  meta: Meta;
  createdAt: number;
  /** 命例名称（用户自定义；旧数据缺省时归一化为"命例 阳历Y-M-D"） */
  name: string;
  /** 备注（用户自定义信息） */
  remark?: string;
  /** 分类标签（用户自定义分类；缺省归入默认分类） */
  category?: string;
  /** 隐藏生辰（中宫展示开关：匿名+时间/四柱置空；仅本地存储，云端 caseBody 白名单不上传） */
  hideBirth?: boolean;
}

/** 兼容旧字段名：历史版本 ChartRecord（无 name/remark/category） */
export type ChartRecord = CaseRecord;

/** 默认分类 */
export const DEFAULT_CATEGORY = '默认';

/** 匿名（未登录）命名空间 */
export const ANON_NS = 'anon';

/** 命名空间标识清洗：仅保留 [A-Za-z0-9_.-]，其余转 _（保证存储 key 稳定安全） */
export function sanitizeNs(ns: string | null | undefined): string {
  return (ns && ns.trim() ? ns.trim() : ANON_NS).replace(/[^A-Za-z0-9_.-]/g, '_');
}

/** 命名空间化 key：`base.ns` */
export function nsKey(base: string, ns: string | null | undefined): string {
  return `${base}.${sanitizeNs(ns)}`;
}

/** 账号桶 LRU 注册表 key（most-recent-first 的 ns 字符串数组） */
export const NS_LRU_KEY = 'paipan.nsLru';
/** 最多保留的账号桶数（超出自动清理最旧账号桶；匿名桶不参与） */
export const MAX_NS_LRU = 1000;

/** 存储抽象：按命名空间（账号/匿名）隔离的全量读写（记录量小，无需增量协议）+ 分类/最近命例元数据 */
export interface ChartStorage {
  /** 切换当前命名空间（按账号/匿名隔离数据；不删除任何桶）；首次调用触发一次性旧数据迁移 */
  useNamespace(ns: string): Promise<void>;
  load(): Promise<CaseRecord[]>;
  save(records: CaseRecord[]): Promise<void>;
  loadCategories(): Promise<string[]>;
  saveCategories(categories: string[]): Promise<void>;
  loadLastChartId(): Promise<string | null>;
  saveLastChartId(chartId: string | null): Promise<void>;
  /** 云端 id 账本（chartInputKey → UserCase.id）：跨重启保留，供同步区分本地新增/他端删除 */
  loadCloudIds(): Promise<Record<string, string>>;
  saveCloudIds(map: Record<string, string>): Promise<void>;
  /** 读取指定命名空间的命例（不切换当前 ns）：登录时并入匿名桶用 */
  peekCases(ns: string): Promise<CaseRecord[]>;
  /** 清空指定命名空间的所有数据：消费匿名桶后清理用 */
  clearNamespace(ns: string): Promise<void>;
}

/** 命例上限（超出删最旧） */
export const MAX_HISTORY = 100;

/** 输入参数唯一键（排盘去重：相同 input 不重复入列） */
export function chartInputKey(input: BirthInput): string {
  return [
    input.calendarType,
    input.year,
    input.month,
    input.day,
    input.hour,
    input.minute,
    input.location,
    input.gender,
    input.isLeapMonth ?? false,
  ].join('|');
}

/** 旧数据归一化：补默认名称/分类（存储升级兼容） */
export function normalizeRecord(r: Partial<CaseRecord> & { chartId: string; input: BirthInput; meta: Meta; createdAt: number }): CaseRecord {
  const [y, m, d] = r.meta?.solar ?? [];
  const name = r.name?.trim() || `命例 ${y ?? ''}-${String(m ?? '').padStart(2, '0')}-${String(d ?? '').padStart(2, '0')}`;
  return {
    chartId: r.chartId,
    input: r.input,
    meta: r.meta,
    createdAt: r.createdAt,
    name,
    remark: r.remark,
    category: r.category || DEFAULT_CATEGORY,
    // 隐藏生辰开关必须进白名单：否则 load() 归一化时被剥掉，刷新/重进页面后丢失
    hideBirth: r.hideBirth || undefined,
  };
}

/** 命例显示摘要（列表用）：阳历 + 性别 + 地点 */
export function caseSummary(r: CaseRecord): string {
  const [y, m, d, h, mi] = r.meta?.solar ?? [r.input.year, r.input.month, r.input.day, r.input.hour, r.input.minute];
  const p = (n: number) => String(n).padStart(2, '0');
  const gender = r.input.gender === 'male' ? '男' : '女';
  const loc = r.input.location || '-';
  // 出生信息一行：YYYY-MM-DD HH:mm 性别 · 地点
  return `${y}-${p(m)}-${p(d)} ${p(h ?? 0)}:${p(mi ?? 0)} ${gender} · ${loc}`;
}

const LS_KEY = 'paipan.caseRecords';
/** 旧版存储 key（历史记录时代）：新 key 无数据时回退读取并自动迁移 */
const LS_LEGACY_KEY = 'paipan.chartRecords';
const LS_CATEGORIES_KEY = 'paipan.categories';
const LS_LAST_ID_KEY = 'paipan.lastChartId';
const LS_CLOUD_IDS_KEY = 'paipan.cloudIds';
const LS_OWNER_UID_KEY = 'paipan.casesOwner';

/** 取全局 localStorage（浏览器/WebView 环境；node 测试等无 window 时返回 null） */
function getLocalStorage(): Storage | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** Web 端实现：localStorage（浏览器验证 / 无插件环境的兜底）；按命名空间隔离 */
export class LocalStorageStorage implements ChartStorage {
  private ns: string = ANON_NS;

  private key(base: string): string {
    return nsKey(base, this.ns);
  }

  async useNamespace(ns: string): Promise<void> {
    await this.migrateLegacyOnce();
    this.ns = sanitizeNs(ns);
    await this.touchNamespaceLru(this.ns);
  }

  /** 记录账号桶最近使用顺序（most-recent-first）；超上限则清理最旧账号桶（匿名桶不参与） */
  private async touchNamespaceLru(ns: string): Promise<void> {
    if (ns === ANON_NS) return;
    const store = getLocalStorage();
    if (!store) return;
    try {
      let list: string[] = [];
      const raw = store.getItem(NS_LRU_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as string[];
        if (Array.isArray(parsed)) list = parsed.filter((x) => typeof x === 'string');
      }
      list = [ns, ...list.filter((x) => x !== ns)];
      const evicted = list.slice(MAX_NS_LRU);
      list = list.slice(0, MAX_NS_LRU);
      store.setItem(NS_LRU_KEY, JSON.stringify(list));
      for (const old of evicted) {
        if (old !== ANON_NS) await this.clearNamespace(old);
      }
    } catch {
      // 静默失败
    }
  }

  /** 一次性把旧的无命名空间数据按 casesOwner 归位到对应 ns（owner→uid:X，否则 anon），迁移后删旧 key */
  private async migrateLegacyOnce(): Promise<void> {
    const store = getLocalStorage();
    if (!store) return;
    const MIG_FLAG = 'paipan.nsMigrated';
    if (store.getItem(MIG_FLAG)) return;
    try {
      const owner = store.getItem(LS_OWNER_UID_KEY);
      const targetNs = owner && owner.trim() ? owner.trim() : ANON_NS;
      const moves: Array<[string, string]> = [
        [LS_KEY, nsKey(LS_KEY, targetNs)],
        [LS_CATEGORIES_KEY, nsKey(LS_CATEGORIES_KEY, targetNs)],
        [LS_LAST_ID_KEY, nsKey(LS_LAST_ID_KEY, targetNs)],
        [LS_CLOUD_IDS_KEY, nsKey(LS_CLOUD_IDS_KEY, targetNs)],
      ];
      for (const [src, dst] of moves) {
        const v = store.getItem(src);
        if (v != null && store.getItem(dst) == null) store.setItem(dst, v);
        store.removeItem(src);
      }
      // 旧历史记录 key（chartRecords）回退迁移到目标 ns 的 caseRecords（若目标为空）
      const legacy = store.getItem(LS_LEGACY_KEY);
      if (legacy != null) {
        const dst = nsKey(LS_KEY, targetNs);
        if (store.getItem(dst) == null) store.setItem(dst, legacy);
        store.removeItem(LS_LEGACY_KEY);
      }
      store.removeItem(LS_OWNER_UID_KEY);
      store.setItem(MIG_FLAG, '1');
    } catch {
      // 迁移失败不阻塞：下次启动重试
    }
  }

  async load(): Promise<CaseRecord[]> {
    try {
      const store = getLocalStorage();
      if (!store) return [];
      const raw = store.getItem(this.key(LS_KEY));
      if (!raw) return [];
      const parsed = JSON.parse(raw) as CaseRecord[];
      return Array.isArray(parsed) ? parsed.map(normalizeRecord) : [];
    } catch {
      return [];
    }
  }

  async save(records: CaseRecord[]): Promise<void> {
    try {
      const store = getLocalStorage();
      if (!store) return;
      store.setItem(this.key(LS_KEY), JSON.stringify(records));
    } catch {
      // 存储不可用（隐私模式等）静默失败，不影响排盘主流程
    }
  }

  async loadCategories(): Promise<string[]> {
    try {
      const store = getLocalStorage();
      if (!store) return [DEFAULT_CATEGORY];
      const raw = store.getItem(this.key(LS_CATEGORIES_KEY));
      if (!raw) return [DEFAULT_CATEGORY];
      const parsed = JSON.parse(raw) as string[];
      const list = Array.isArray(parsed) ? parsed.filter((c) => typeof c === 'string' && c.trim()) : [];
      return list.length ? list : [DEFAULT_CATEGORY];
    } catch {
      return [DEFAULT_CATEGORY];
    }
  }

  async saveCategories(categories: string[]): Promise<void> {
    try {
      const store = getLocalStorage();
      if (!store) return;
      const list = categories.length ? categories : [DEFAULT_CATEGORY];
      store.setItem(this.key(LS_CATEGORIES_KEY), JSON.stringify(list));
    } catch {
      // 静默失败
    }
  }

  async loadLastChartId(): Promise<string | null> {
    try {
      const store = getLocalStorage();
      if (!store) return null;
      return store.getItem(this.key(LS_LAST_ID_KEY));
    } catch {
      return null;
    }
  }

  async saveLastChartId(chartId: string | null): Promise<void> {
    try {
      const store = getLocalStorage();
      if (!store) return;
      if (chartId) store.setItem(this.key(LS_LAST_ID_KEY), chartId);
      else store.removeItem(this.key(LS_LAST_ID_KEY));
    } catch {
      // 静默失败
    }
  }

  async loadCloudIds(): Promise<Record<string, string>> {
    try {
      const store = getLocalStorage();
      if (!store) return {};
      const raw = store.getItem(this.key(LS_CLOUD_IDS_KEY));
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Record<string, string>;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  async saveCloudIds(map: Record<string, string>): Promise<void> {
    try {
      const store = getLocalStorage();
      if (!store) return;
      store.setItem(this.key(LS_CLOUD_IDS_KEY), JSON.stringify(map));
    } catch {
      // 静默失败
    }
  }

  async peekCases(ns: string): Promise<CaseRecord[]> {
    try {
      const store = getLocalStorage();
      if (!store) return [];
      const raw = store.getItem(nsKey(LS_KEY, ns));
      if (!raw) return [];
      const parsed = JSON.parse(raw) as CaseRecord[];
      return Array.isArray(parsed) ? parsed.map(normalizeRecord) : [];
    } catch {
      return [];
    }
  }

  async clearNamespace(ns: string): Promise<void> {
    try {
      const store = getLocalStorage();
      if (!store) return;
      for (const base of [LS_KEY, LS_CATEGORIES_KEY, LS_LAST_ID_KEY, LS_CLOUD_IDS_KEY]) {
        store.removeItem(nsKey(base, ns));
      }
    } catch {
      // 静默失败
    }
  }
}
